<template>
  <div class="flex w-full h-full" :class="layout === 'horizontal' ? 'flex-row' : 'flex-col'">
    <!-- First Section -->
    <div
      ref="header"
      :class="[
        'flex  flex-col min-w-[100px] min-h-[180px]',
        layout === 'horizontal' ? 'flex-grow h-full overflow-hidden resize-x' : 'resize-y',
      ]">
      <div class="flex-shrink-0" v-if="$slots.headerTitle">
        <slot name="headerTitle"></slot>
      </div>

      <div class="overflow-auto" :class="layout === 'horizontal' ? 'flex-grow h-0' : 'h-full'">
        <slot name="headerContent"></slot>
      </div>
    </div>

    <!-- Divider -->
    <div
      ref="divider"
      class="z-10 bg-gray-100 hover:bg-gray-400 shrink-0"
      :class="layout === 'horizontal' ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize'"
      @mousedown="onMouseDown"></div>

    <!-- Second Section -->
    <div
      ref="details"
      :class="[
        'flex flex-col min-w-[100px] min-h-[100px]',
        layout === 'horizontal'
          ? 'flex-grow h-full overflow-hidden resize-x'
          : 'flex-grow overflow-auto resize-y',
      ]">
      <div class="flex-shrink-0" v-if="$slots.detailsTitle">
        <slot name="detailsTitle"></slot>
      </div>
      <div class="flex-grow overflow-x-hidden">
        <slot name="detailsContent"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue';

const props = withDefaults(
  defineProps<{
    layout: 'horizontal' | 'vertical';
    minSize?: number;
  }>(),
  {
    minSize: 300,
  }
);

const header = ref<HTMLElement | null>(null);
const details = ref<HTMLElement | null>(null);
const divider = ref<HTMLElement | null>(null);

const updateSizes = async () => {
  await nextTick();
  const containerEl = divider.value?.parentElement as HTMLElement;
  if (!containerEl) return;

  const headerEl = header.value!;
  const detailsEl = details.value!;

  if (props.layout === 'horizontal') {
    headerEl.style.width = '50%';
    headerEl.style.height = '100%';
    detailsEl.style.width = '50%';
    detailsEl.style.height = '100%';
  } else {
    headerEl.style.height = 'auto';
    headerEl.style.width = '100%';

    detailsEl.style.height = 'auto';
    detailsEl.style.minHeight = props.minSize + 'px';
    detailsEl.style.width = '100%';
  }
};

const onMouseDown = (e: MouseEvent) => {
  e.preventDefault();
  const isHorizontal = props.layout === 'horizontal';
  const startPos = isHorizontal ? e.pageX : e.pageY;

  const headerEl = header.value!;
  const detailsEl = details.value!;

  const startSize1 = isHorizontal ? headerEl.offsetWidth : headerEl.offsetHeight;
  const startSize2 = isHorizontal ? detailsEl.offsetWidth : detailsEl.offsetHeight;

  const onMouseMove = (e: MouseEvent) => {
    const currentPos = isHorizontal ? e.pageX : e.pageY;
    const diff = currentPos - startPos;
    const newSize1 = startSize1 + diff;
    const newSize2 = startSize2 - diff;

    if (newSize1 >= props.minSize && newSize2 >= props.minSize) {
      if (isHorizontal) {
        headerEl.style.width = newSize1 + 'px';
        detailsEl.style.width = newSize2 + 'px';
      } else {
        headerEl.style.height = newSize1 + 'px';
        detailsEl.style.height = newSize2 + 'px';
      }
    }
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};

onMounted(updateSizes);
watch(() => props.layout, updateSizes);
</script>
